import { useState } from 'react';
import './Survey.css';

interface SurveyQuestion {
  id: string;
  category: 'mind' | 'diet' | 'exercise' | 'general' | 'local' | 'schedule';
  question: string;
  subtext?: string;
  type: 'text' | 'select' | 'multiselect' | 'number' | 'zipcode' | 'daily_schedule';
  options?: string[];
  optional?: boolean;
}

const SCHEDULE_SLOTS = [
  {
    key: 'wake_up',
    label: 'Wake up',
    options: ['4am', '5am', '5:30am', '6am', '6:30am', '7am', '7:30am', '8am', '8:30am', '9am', '10am', '11am or later'],
  },
  {
    key: 'breakfast',
    label: 'Breakfast',
    options: ['5am', '6am', '7am', '7:30am', '8am', '8:30am', '9am', '9:30am', '10am', 'Later', 'I skip breakfast'],
  },
  {
    key: 'lunch',
    label: 'Lunch',
    options: ['10am', '11am', '11:30am', '12pm', '12:30pm', '1pm', '1:30pm', '2pm', 'Later', 'I skip lunch'],
  },
  {
    key: 'dinner',
    label: 'Dinner',
    options: ['4pm', '5pm', '5:30pm', '6pm', '6:30pm', '7pm', '7:30pm', '8pm', '9pm or later'],
  },
  {
    key: 'exercise',
    label: 'Exercise',
    options: ['Before 7am', '7–10am', '10am–1pm', '1–5pm', '5–8pm', 'After 8pm', 'Varies', "I don't exercise regularly"],
  },
  {
    key: 'wind_down',
    label: 'Wind down / sleep',
    options: ['8pm', '8:30pm', '9pm', '9:30pm', '10pm', '10:30pm', '11pm', 'Midnight or later'],
  },
];


const surveyQuestions: SurveyQuestion[] = [
  // -----------------------------
  // DAILY SCHEDULE (first)
  // -----------------------------
  {
    id: 'daily_schedule',
    category: 'schedule',
    question: 'What does your typical day look like?',
    subtext: "We'll use this to time check-ins around your actual schedule and know what's realistic for you.",
    type: 'daily_schedule',
  },

  // -----------------------------
  // DIET
  // -----------------------------
  {
    id: 'diet_satisfaction',
    category: 'diet',
    question: 'How satisfied are you with your current diet and food habits?',
    type: 'select',
    options: [
      'I\'m happy with my status quo',
      'I want to make some minor changes',
      'I want to make major changes',
    ],
  },
  {
    id: 'diet_grocery_day',
    category: 'diet',
    question: 'When do you usually do your grocery shopping (including online ordering)?',
    type: 'multiselect',
    options: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Varies week-to-week',
    ],
  },
  {
    id: 'diet_cuisines_enjoy',
    category: 'diet',
    question: 'What types of cuisine do you enjoy or want to try more of? (Select all that apply)',
    type: 'multiselect',
    options: [
      'American',
      'Mediterranean',
      'Italian',
      'Mexican',
      'Asian (Chinese, Japanese, Thai, etc.)',
      'Indian',
      'Middle Eastern',
      'French',
      'Spanish',
      'Plant-based / vegan',
      'Seafood-focused',
      'No preference / open to anything',
    ],
  },
  {
    id: 'diet_restrictions',
    category: 'diet',
    question: 'Do you have any dietary restrictions, allergies, or preferences? (e.g., vegetarian, gluten-free, lactose intolerant)',
    type: 'text',
    optional: true,
  },
  {
    id: 'diet_cooking_frequency',
    category: 'diet',
    question: 'How often do you cook at home?',
    type: 'select',
    options: [
      'Rarely — mostly takeout or delivery',
      'A couple of times a week',
      'Most nights',
      'Almost always',
    ],
  },
  {
    id: 'diet_cooking_time',
    category: 'diet',
    question: 'On a typical night, how much time do you have to cook?',
    type: 'select',
    options: [
      'Under 15 minutes',
      '15–30 minutes',
      '30–45 minutes',
      '45 minutes or more',
      "I don't cook",
    ],
  },
  {
    id: 'general_household',
    category: 'diet',
    question: 'Who do you usually cook for or eat with?',
    type: 'select',
    options: [
      'Just me',
      'Me and a partner',
      'Family with kids',
      'Roommates / shared household',
      'Varies a lot',
    ],
  },
  {
    id: 'diet_goals',
    category: 'diet',
    question: 'Which of the following nutrition goals are most important to you right now? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Eat fewer ultra-processed snacks and convenience foods',
      'Cook more meals at home with simple, whole ingredients',
      'Cut back on added sugar for more stable energy',
      'Plan meals ahead to make healthy choices feel easier',
      'Add more fruits and vegetables into my daily routine',
    ],
  },
  {
    id: 'diet_additional',
    category: 'diet',
    question: 'Is there anything else you want to share about your current eating habits? Feel free to include any additional goals, changes you\'d like to make, preferences, or patterns.',
    type: 'text',
    optional: true,
  },

  // -----------------------------
  // MIND
  // -----------------------------
  {
    id: 'mind_describe',
    category: 'mind',
    question: 'How would you describe your overall mental and emotional wellbeing?',
    type: 'select',
    options: [
      'I feel good overall',
      'I feel okay and want to improve in some areas',
      'I often don\'t feel my best and want meaningful change',
    ],
  },
  {
    id: 'mind_sleep',
    category: 'mind',
    question: 'Which statement best matches your current sleep routine?',
    type: 'select',
    options: [
      'Consistent sleep and wake times',
      'Somewhat inconsistent',
      'Very inconsistent and want to improve',
    ],
  },
  {
    id: 'mind_sleep_hours',
    category: 'mind',
    question: 'On average, how many hours of sleep do you get per night?',
    type: 'select',
    options: ['Less than 5', '6', '7', '8', 'More than 8'],
  },
  {
    id: 'mind_stress_timing',
    category: 'mind',
    question: 'When do you typically feel stress?',
    type: 'select',
    options: [
      'During weekdays (work, school, etc.)',
      'On weekends',
      'Both equally',
      'Other / no clear pattern',
    ],
  },
  {
    id: 'mind_stress_relief',
    category: 'mind',
    question: 'Which stress relief strategies do you already practice or are interested in? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Exercise or physical activity',
      'Meditation',
      'Deep breathing exercises',
      'Journaling',
      'Time outdoors',
      'Music or calming audio',
      'Quality time with friends or family',
      'Therapy',
      'Reading',
      'Yoga or stretching',
      'Creative hobbies (art, music, crafts)',
      'Light movement breaks (walking, mobility, stretching)',
      'Spending time with pets',
    ],
  },
  {
    id: 'mind_activities',
    category: 'mind',
    question: 'What activities are part of your typical week? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Working',
      'Attending school or classes',
      'Parenting or childcare',
      'Caretaking for a family member',
      'Reading',
      'Writing or journaling',
      'Creative hobbies (art, design, music, crafts)',
      'Puzzles or brain games (crosswords, sudoku, etc.)',
      'Strategy or board games',
      'Spending time outdoors',
      'Social time with friends or family',
      'Other',
    ],
  },
  {
    id: 'mind_wellness_goals',
    category: 'mind',
    question: 'Which of the following mental wellness goals matter most to you right now? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Building a relaxing nighttime routine',
      'Getting more consistent, restful sleep',
      'Spending less time on my phone or screens',
      'Being more intentional about connecting with friends and family',
      'Spending more time outdoors and in nature',
      'Keeping my mind sharp with puzzles, reading, or learning new skills',
      'Developing a regular mindfulness or meditation practice',
    ],
  },
  {
    id: 'mind_goals',
    category: 'mind',
    question: 'Anything else you\'d like to share about your mental wellness? Feel free to include any additional goals or preferences.',
    type: 'text',
    optional: true,
  },

  // -----------------------------
  // EXERCISE
  // -----------------------------
  {
    id: 'move_fitness_level',
    category: 'exercise',
    question: 'How would you describe your current fitness level and workout intensity?',
    type: 'select',
    options: [
      'Just getting started — I\'m new to exercise or returning after a long break',
      'Beginner — I do light activity but want to build a routine',
      'Intermediate — I work out regularly and want to keep progressing',
      'Advanced — I train hard and want intense, challenging workouts',
    ],
  },
  {
    id: 'move_types',
    category: 'exercise',
    question: 'Which types of exercise or movement do you enjoy or prefer? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Walking',
      'Running',
      'Cycling',
      'Strength training',
      'Yoga or Pilates',
      'Sports or recreational activities',
      'Hiking or outdoor activities',
      'Swimming or water workouts',
      'At-home workouts',
      'Dance',
      'No preference / unsure',
    ],
  },
  {
    id: 'move_types_exclude',
    category: 'exercise',
    question: 'Which types of exercise do you specifically not want recommended, or have a physical limitation that prevents? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Walking',
      'Running',
      'Cycling',
      'Strength training',
      'Yoga or Pilates',
      'Sports or recreational activities',
      'Hiking or outdoor activities',
      'Swimming or water workouts',
      'At-home workouts',
      'Dance',
      'No preference / unsure',
    ],
  },
  {
    id: 'move_daily_activity',
    category: 'exercise',
    question: 'On a typical day, roughly how many hours do you spend sitting? (desk, commute, couch, etc.)',
    type: 'select',
    options: [
      'Less than 2 hours',
      '2–4 hours',
      '4–6 hours',
      '6–8 hours',
      'More than 8 hours',
    ],
  },
  {
    id: 'move_exercise_goals',
    category: 'exercise',
    question: 'Which of the following movement goals are most important to you right now? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Moving my body more throughout the day',
      'Building a consistent cardio routine',
      'Starting or improving a strength-training routine',
      'Improving flexibility or mobility',
      'Recovering better between workouts',
      'Making movement feel more enjoyable',
      'Joining a group or community workout class',
    ],
  },
  {
    id: 'move_goals',
    category: 'exercise',
    question: 'Anything else you\'d like to share about your movement or exercise habits? Feel free to include any additional goals or preferences.',
    type: 'text',
    optional: true,
  },

  // -----------------------------
  // LOCAL SUGGESTIONS
  // -----------------------------
  {
    id: 'zip_code',
    category: 'local',
    question: "What's your zip code?",
    subtext: "We'll use this to include local weather and nearby event suggestions in your daily check-ins. Totally optional — skip if you'd prefer not to share.",
    type: 'zipcode',
    optional: true,
  },
  {
    id: 'local_event_types',
    category: 'local',
    question: 'What kinds of local activities interest you?',
    subtext: "On Thursdays we'll surface local events near you that match your interests. Select as many as you like, or skip.",
    type: 'multiselect',
    options: [
      'Live Music & Concerts',
      'Food & Drink Events (festivals, tastings, markets)',
      'Family & Kid-Friendly',
      'Outdoors & Nature (hikes, walks, parks, markets)',
      'Fitness & Wellness (yoga, group runs, classes)',
      'Arts & Culture (museums, galleries, theater, comedy)',
      'Festivals & Seasonal Events',
      'Workshops & Classes (cooking, crafts, learning)',
      'Sports (watching or participating)',
      'Community & Local Gatherings (farmers markets, library events, meetups)',
    ],
    optional: true,
  },

  // -----------------------------
  // GENERAL
  // -----------------------------
  {
    id: 'general_additional',
    category: 'general',
    question: 'Is there anything else you\'d like to share that you think would be helpful for us to know?',
    type: 'text',
    optional: true,
  },
];

function SurveyDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [done, setDone] = useState(false);

  const totalSteps = surveyQuestions.length;
  const currentQuestion = surveyQuestions[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleTextChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNumberChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleSelectChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleMultiSelectChange = (value: string) => {
    setResponses(prev => {
      const currentValues = (prev[currentQuestion.id] as string[]) || [];
      if (currentValues.includes(value)) {
        return { ...prev, [currentQuestion.id]: currentValues.filter(v => v !== value) };
      }
      return { ...prev, [currentQuestion.id]: [...currentValues, value] };
    });
  };

  const handleScheduleChange = (field: string, value: string) => {
    setResponses(prev => ({ ...prev, [`schedule_${field}`]: value }));
  };

  const canProceed = () => {
    if (currentQuestion.type === 'daily_schedule') {
      return SCHEDULE_SLOTS.every(slot => !!(responses[`schedule_${slot.key}`] as string));
    }
    if (currentQuestion.type === 'zipcode') {
      const val = (responses[currentQuestion.id] as string) || '';
      return val.length === 0 || val.length === 5;
    }
    if (currentQuestion.optional) return true;
    const response = responses[currentQuestion.id];
    if (!response) return false;
    if (Array.isArray(response)) return response.length > 0;
    return response.trim().length > 0;
  };

  const handleNext = () => {
    if (currentStep < surveyQuestions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    setDone(true);
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'schedule': return 'Daily Schedule';
      case 'mind': return 'Mind & Mental Wellness';
      case 'diet': return 'Diet & Nutrition';
      case 'exercise': return 'Exercise & Movement';
      case 'local': return 'Local Suggestions (Optional)';
      default: return 'General';
    }
  };

  if (done) {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="survey-header">
            <h1>That's it!</h1>
            <p>In the real flow your responses would be saved and you'd be taken to the welcome screen.</p>
          </div>
          <div className="question-card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '1.1rem', color: '#555' }}>This is a demo — no data was submitted.</p>
            <button
              className="btn btn-primary"
              style={{ marginTop: '1.5rem' }}
              onClick={() => {
                setDone(false);
                setCurrentStep(0);
                setResponses({});
              }}
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      <div className="survey-container">
        <div className="survey-header">
          <h1>Let's Get to Know You</h1>
          <p>Your answers will help personalize your wellness journey</p>
        </div>

        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="progress-text">
          {`Question ${currentStep + 1} of ${totalSteps}`}
        </div>

        <div className="question-card">
            <div className="question-category">
              <span className="category-label">{getCategoryLabel(currentQuestion.category)}</span>
            </div>

            <h2 className="question-text">{currentQuestion.question}</h2>

            {currentQuestion.subtext && (
              <p className="question-subtext">{currentQuestion.subtext}</p>
            )}

            <div className="answer-section">
              {currentQuestion.type === 'daily_schedule' && (
                <div className="daily-schedule">
                  {SCHEDULE_SLOTS.map(slot => {
                    const value = (responses[`schedule_${slot.key}`] as string) || '';
                    return (
                      <div key={slot.key} className="schedule-row">
                        <span className="schedule-label">{slot.label}</span>
                        <select
                          className={`schedule-select${value ? ' filled' : ''}`}
                          value={value}
                          onChange={e => handleScheduleChange(slot.key, e.target.value)}
                        >
                          <option value="">—</option>
                          {slot.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}

              {currentQuestion.type === 'zipcode' && (
                <div className="zipcode-input-wrapper">
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    value={(responses[currentQuestion.id] as string) || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                      handleTextChange(val);
                    }}
                    placeholder="e.g. 10001"
                    className="zipcode-input"
                  />
                  {(() => {
                    const val = (responses[currentQuestion.id] as string) || '';
                    if (val.length > 0 && val.length < 5) {
                      return <p className="input-warning">Please enter a 5-digit zip code.</p>;
                    }
                    return null;
                  })()}
                </div>
              )}

              {currentQuestion.type === 'text' && (
                <>
                  <textarea
                    value={(responses[currentQuestion.id] as string) || ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder="Type your answer here..."
                    rows={4}
                  />
                  <p className="input-disclaimer">
                    Please do not include sensitive personal information such as health conditions, medical history, Social Security numbers, financial details, or other private data in your response.
                  </p>
                </>
              )}

              {currentQuestion.type === 'number' && (
                <input
                  type="number"
                  value={(responses[currentQuestion.id] as string) || ''}
                  onChange={(e) => handleNumberChange(e.target.value)}
                  placeholder="Enter a number"
                  className="number-input"
                />
              )}

              {currentQuestion.type === 'select' && (
                <div className="select-options">
                  {currentQuestion.options?.map((option) => (
                    <button
                      key={option}
                      className={`option-btn ${responses[currentQuestion.id] === option ? 'selected' : ''}`}
                      onClick={() => handleSelectChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'multiselect' && (
                <div className="multiselect-options">
                  {currentQuestion.options?.map((option) => (
                    <button
                      key={option}
                      className={`option-btn ${((responses[currentQuestion.id] as string[]) || []).includes(option) ? 'selected' : ''}`}
                      onClick={() => handleMultiSelectChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

        <div className="survey-navigation">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="btn btn-secondary"
          >
            Previous
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="btn btn-primary"
          >
            {currentStep === surveyQuestions.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SurveyDemo;
